import { FC, useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Autocomplete, Alert } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { createLabelTag, updateLabelTag } from "../../../config/axios/apis/labelTags/labelTagsAPI";
import { success } from "../../../utils/AppUtils";
import { uiActions } from "../../../reducers/uiReducer";
import { LabelCategoryOption, CreateLabelFormValues, LabelDetails } from "../../../config/axios/apis/labelTags/types";

interface CreateLabelModalProps {
    open: boolean;
    categories: LabelCategoryOption[];
    onClose: () => void;
    labelDetails?: LabelDetails | null;
}

const schema: yup.ObjectSchema<CreateLabelFormValues> = yup.object({
    name: yup.string().trim().required("Label Name is required").max(64, "Label Name cannot exceed 64 characters"),
    category: yup.mixed<LabelCategoryOption>().nullable().required("Category is required"),
});

const CreateLabelModal: FC<CreateLabelModalProps> = ({ open, categories, onClose, labelDetails }) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        handleSubmit,
        control,
        formState: { dirtyFields, isValid },
        reset,
    } = useForm<CreateLabelFormValues>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: { name: "", category: null },
    });

    useEffect(() => {
        if (open) {
            if (labelDetails) {
                reset({
                    name: labelDetails.name || "",
                    category: labelDetails.category || null,
                });
            } else {
                reset({ name: "", category: null });
            }
        }
    }, [open, labelDetails, reset]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit: SubmitHandler<CreateLabelFormValues> = (data) => {
        setIsSubmitting(true);

        const request = labelDetails?.id
            ? updateLabelTag(labelDetails.id, { name: data.name })
            : createLabelTag({ name: data.name, type: data.category?.value || "" });

        request
            .then(() => {
                const messageId = labelDetails?.id ? "label-tags.success.label-tag-created" : "label-tags.message.created-label-tag";
                const defaultMsg = labelDetails?.id ? "Label tag has been successfully updated" : "Label tag has been successfully created";

                success(<FormattedMessage id={messageId} defaultMessage={defaultMsg} />);
                dispatch(uiActions.setForceRefreshGrid(true));
                handleClose();
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };
    const isEdit = !!labelDetails?.id;
    const isButtonDisabled = isSubmitting || !isValid || (isEdit && !dirtyFields.name);

    const getButtonLabel = () => {
        if (isSubmitting) return "...";

        return isEdit ? (
            <FormattedMessage id="create-label-tag.button.update" defaultMessage="Update Label" />
        ) : (
            <FormattedMessage id="common.button-label.create-label" defaultMessage="Create Label" />
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600 }}>
                <FormattedMessage
                    id={isEdit ? "update-label-tag.title" : "create-label-tag.title"}
                    defaultMessage={isEdit ? "Update Label Tag" : "Create Label Tag"}
                />
            </DialogTitle>
            <DialogContent dividers>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {!isEdit && (
                        <Alert iconMapping={{ info: <InfoIcon fontSize="large" /> }} severity="info">
                            <FormattedMessage
                                id="create-label-tag.label.description"
                                defaultMessage="Add a new label to an existing category. Categories cannot be created by users."
                            />
                        </Alert>
                    )}

                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { invalid, error } }) => (
                            <TextField
                                {...field}
                                label={formatMessage({ id: "create-label-tag.label.field-name", defaultMessage: "Label Name" })}
                                placeholder={formatMessage({ id: "create-label-tag.placeholder.field-name", defaultMessage: "Enter label name" })}
                                fullWidth
                                error={invalid}
                                helperText={
                                    invalid && error ? (
                                        <FormattedMessage id={`create-label-tag.field-name.error.${error.type}`} defaultMessage={error.message} />
                                    ) : (
                                        " "
                                    )
                                }
                            />
                        )}
                    />

                    <Controller
                        name="category"
                        control={control}
                        render={({ field, fieldState: { invalid, error } }) => (
                            <Autocomplete
                                {...field}
                                options={categories}
                                disabled={isEdit}
                                onChange={(_, value) => field.onChange(value)}
                                // todo this is comparing the names but it should compare ids just in case there are categories with the same name
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                getOptionLabel={(option) => option.label || ""}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={formatMessage({ id: "common.field-label.category", defaultMessage: "Category" })}
                                        error={invalid}
                                        helperText={
                                            invalid && error ? (
                                                <FormattedMessage id={`create-label-tag.label-category.error.${error.type}`} defaultMessage={error.message} />
                                            ) : (
                                                " "
                                            )
                                        }
                                    />
                                )}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} disabled={isSubmitting}>
                    <FormattedMessage id="common.button-label.cancel" defaultMessage="Cancel" />{" "}
                </Button>
                <Button onClick={handleSubmit(onSubmit)} disabled={isButtonDisabled}>
                    {getButtonLabel()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateLabelModal;
